import React from 'react';
import useScrollReveal from '../hooks/useScrollReveal';

function TimelineItem({ event, index }) {
  const [ref, visible] = useScrollReveal(0.15);
  const reverse = index % 2 === 1;

  // Slide-in direction: alternate left/right
  const hiddenTransform = reverse ? 'translateX(-40px)' : 'translateX(40px)';

  return (
    <div
      ref={ref}
      data-testid={`timeline-item-${index}`}
      className={`relative flex flex-col md:flex-row ${
        reverse ? 'md:flex-row-reverse' : ''
      }`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : hiddenTransform,
        transition: 'opacity 0.9s ease-out, transform 0.9s ease-out',
      }}
    >
      {/* Center dot */}
      <div className="absolute start-6 md:start-1/2 top-2 -translate-x-1/2 rtl:translate-x-1/2">
        <div
          className="h-3 w-3 rounded-full border-2 border-primary bg-background"
          style={{ boxShadow: 'var(--shadow-gold)' }}
        />
      </div>

      {/* Card */}
      <div
        className={`ms-16 md:ms-0 md:w-1/2 ${
          reverse ? 'md:ps-16 md:text-start' : 'md:pe-16 md:text-end'
        }`}
      >
        <div className="bg-glass rounded-lg p-6">
          <span className="text-gradient-gold font-display text-3xl font-bold md:text-4xl">
            {event.year}
          </span>
          <h3 className="mt-2 font-arabic text-lg font-semibold text-foreground">
            {event.title}
          </h3>
          <p className="mt-1 text-sm text-foreground/60">{event.desc}</p>
        </div>
      </div>
    </div>
  );
}

export default function Timeline({ t }) {
  const [titleRef, titleVisible] = useScrollReveal(0.2);

  return (
    <section className="py-24 md:py-32">
      <div className="container mx-auto max-w-4xl px-6">
        <div
          ref={titleRef}
          className="mb-16 text-center"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.9s ease-out, transform 0.9s ease-out',
          }}
        >
          <h2
            data-testid="timeline-title"
            className="text-gradient-gold font-arabic text-3xl font-bold md:text-4xl leading-[1.6] pb-2"
          >
            {t.timeline.title}
          </h2>
          <div className="mt-4 gold-divider mx-auto w-16" />
        </div>

        <div className="relative">
          {/* Center vertical line */}
          <div className="absolute top-0 bottom-0 start-6 md:start-1/2 w-px bg-gradient-to-b from-transparent via-primary/30 to-transparent" />

          <div className="space-y-16">
            {t.timeline.events.map((event, i) => (
              <TimelineItem key={i} event={event} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
